import React, { useState } from 'react';
import { Search, Star, BookOpen, Music, X, ArrowLeft } from 'lucide-react';
import { POEMS } from '../data/mockData';
import { Poem } from '../types';
type PoemLibraryProps = {
  onSelectPoem: (poem: Poem) => void;
};
export function PoemLibrary({
  onSelectPoem
}: PoemLibraryProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDynasty, setFilterDynasty] = useState<string>('all');
  const [selectedPoem, setSelectedPoem] = useState<Poem | null>(null);
  const filteredPoems = POEMS.filter(poem => {
    const matchesSearch = poem.title.includes(searchTerm) || poem.author.includes(searchTerm);
    const matchesDynasty = filterDynasty === 'all' || poem.dynasty === filterDynasty;
    return matchesSearch && matchesDynasty;
  });
  const dynasties = ['all', ...Array.from(new Set(POEMS.map(p => p.dynasty)))];
  // Poem detail modal
  if (selectedPoem) {
    return <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-494">
        {/* Back button */}
        <button onClick={() => setSelectedPoem(null)} className="flex items-center text-gray-500 hover:text-gray-900 transition-colors mb-8 group" data-id="element-495">
          <div className="p-1 rounded-full bg-gray-100 group-hover:bg-gray-200 mr-2 transition-colors" data-id="element-496">
            <ArrowLeft className="w-4 h-4" data-id="element-497" />
          </div>
          返回诗词库
        </button>

        {/* Poem detail card */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden" data-id="element-498">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#C41E3A] to-[#E91E63] p-8 text-white" data-id="element-499">
            <div className="flex justify-between items-start mb-4" data-id="element-500">
              <div data-id="element-501">
                <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-sm mb-3" data-id="element-502">
                  {selectedPoem.dynasty} · {selectedPoem.author}
                </span>
                <h1 className="text-4xl font-bold" data-id="element-503">{selectedPoem.title}</h1>
              </div>
              <div className="flex items-center bg-white/20 px-3 py-2 rounded-lg" data-id="element-504">
                <Star className="w-5 h-5 text-yellow-300 fill-current" data-id="element-505" />
                <span className="ml-2 font-bold" data-id="element-506">
                  难度 {selectedPoem.difficulty}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4 text-white/80" data-id="element-507">
              <span className="flex items-center" data-id="element-508">
                <Music className="w-4 h-4 mr-1" data-id="element-509" />
                {selectedPoem.mode}
              </span>
              <span data-id="element-510">节拍: {selectedPoem.timeSignature}</span>
            </div>
          </div>

          {/* Poem content */}
          <div className="p-8" data-id="element-511">
            <h2 className="text-lg font-bold text-gray-500 mb-6 text-center" data-id="element-512">
              诗词原文
            </h2>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 mb-8" data-id="element-513">
              <div className="text-center space-y-4" data-id="element-514">
                {selectedPoem.content.map((line, index) => <p key={index} className="text-2xl md:text-3xl font-serif text-gray-800 leading-relaxed tracking-wider" data-id="element-515">
                    {line}
                  </p>)}
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-516">
              <button onClick={() => onSelectPoem(selectedPoem)} className="flex items-center justify-center px-8 py-4 bg-[#C41E3A] text-white rounded-full font-bold text-lg shadow-lg hover:bg-red-700 transition-all hover:scale-105" data-id="element-517">
                <Music className="w-5 h-5 mr-2" data-id="element-518" />
                开始音乐练习
              </button>
              <button onClick={() => setSelectedPoem(null)} className="flex items-center justify-center px-8 py-4 bg-gray-100 text-gray-700 rounded-full font-bold text-lg hover:bg-gray-200 transition-all" data-id="element-519">
                浏览其他诗词
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-520">
      <div className="text-center mb-12" data-id="element-521">
        <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-522">古诗词库</h2>
        <p className="text-xl text-gray-600" data-id="element-523">精选适合儿童吟唱的经典古诗</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4" data-id="element-524">
        <div className="relative w-full md:w-96" data-id="element-525">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" data-id="element-526" />
          <input type="text" placeholder="搜索诗名或作者..." className="w-full pl-10 pr-4 py-3 rounded-full border-2 border-gray-200 focus:border-[#C41E3A] focus:outline-none transition-colors" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-527" />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 w-full md:w-auto" data-id="element-528">
          {dynasties.map(dynasty => <button key={dynasty} onClick={() => setFilterDynasty(dynasty)} className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${filterDynasty === dynasty ? 'bg-[#C41E3A] text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`} data-id="element-529">
              {dynasty === 'all' ? '全部朝代' : dynasty}
            </button>)}
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-530">
        {filteredPoems.map(poem => <div key={poem.id} className="bg-white rounded-3xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 border-2 border-transparent hover:border-[#C41E3A]/20 cursor-pointer group" onClick={() => setSelectedPoem(poem)} data-id="element-531">
            <div className="flex justify-between items-start mb-4" data-id="element-532">
              <div data-id="element-533">
                <span className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md mb-2 font-medium" data-id="element-534">
                  {poem.dynasty} · {poem.author}
                </span>
                <h3 className="text-2xl font-bold text-gray-900 group-hover:text-[#C41E3A] transition-colors" data-id="element-535">
                  {poem.title}
                </h3>
              </div>
              <div className="flex items-center bg-yellow-50 px-2 py-1 rounded-lg" data-id="element-536">
                <Star className="w-4 h-4 text-yellow-500 fill-current" data-id="element-537" />
                <span className="ml-1 text-sm font-bold text-yellow-700" data-id="element-538">
                  {poem.difficulty}
                </span>
              </div>
            </div>

            {/* Show full poem content preview */}
            <div className="mb-6 p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl" data-id="element-539">
              <div className="space-y-1" data-id="element-540">
                {poem.content.slice(0, 4).map((line, index) => <p key={index} className="text-gray-700 font-serif text-base leading-relaxed" data-id="element-541">
                    {line}
                  </p>)}
                {poem.content.length > 4 && <p className="text-gray-400 text-sm" data-id="element-542">
                    ...共{poem.content.length}句
                  </p>}
              </div>
            </div>

            <div className="flex justify-between items-center" data-id="element-543">
              <div className="flex items-center text-sm text-gray-500" data-id="element-544">
                <Music className="w-4 h-4 mr-1" data-id="element-545" />
                {poem.mode}
              </div>
              <button className="text-[#C41E3A] font-bold text-sm flex items-center group-hover:translate-x-1 transition-transform" data-id="element-546">
                查看详情 <BookOpen className="w-4 h-4 ml-1" data-id="element-547" />
              </button>
            </div>
          </div>)}
      </div>
    </div>;
}